import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const MarketplaceSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      setPaymentDetails(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div data-testid="marketplace-success-page" className="max-w-2xl mx-auto pt-12">
      <Card className="bg-[#1E293B] border-[#334155]">
        <CardHeader>
          <div className="text-center">
            {status === 'checking' && (
              <>
                <Loader2 className="w-16 h-16 text-[#F59E0B] mx-auto mb-4 animate-spin" />
                <CardTitle className="text-2xl text-white mb-2">Processing Payment</CardTitle>
                <CardDescription className="text-zinc-400">Please wait while we verify your payment...</CardDescription>
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
                <CardTitle className="text-2xl text-white mb-2">Payment Successful!</CardTitle>
                <CardDescription className="text-zinc-400">Your purchase has been completed</CardDescription>
              </>
            )}
            {(status === 'error' || status === 'timeout') && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-900/20 border-2 border-red-500 flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl text-red-500">×</span>
                </div>
                <CardTitle className="text-2xl text-white mb-2">Payment Issue</CardTitle>
                <CardDescription className="text-zinc-400">
                  {status === 'timeout'
                    ? 'Payment verification timed out. Please check your email for confirmation.'
                    : 'There was an issue with your payment. Please try again.'}
                </CardDescription>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {paymentDetails && status === 'success' && (
            <div className="bg-[#0F172A] rounded-lg p-6 border border-[#334155] space-y-2">
              <div className="text-sm text-zinc-400">Amount Paid</div>
              <div className="text-3xl font-bold text-[#10B981]">
                ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}
              </div>
            </div>
          )}
          <div className="flex gap-4 justify-center pt-4">
            <Button
              data-testid="back-to-marketplace-btn"
              onClick={() => navigate('/marketplace')}
              className="bg-[#334155] hover:bg-[#F59E0B] hover:text-[#0F172A] transition-colors font-bold"
            >
              Back to Marketplace
            </Button>
            <Button
              data-testid="dashboard-btn"
              onClick={() => navigate('/dashboard')}
              className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold"
            >
              Go to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarketplaceSuccess;
